import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { AnimatedSection } from '../../../lib/animations/AnimatedSection';

function FAQItem({ q, a, accent, textPrimary, textSecondary, border }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: `1px solid ${border}` }}>
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between py-4 text-left">
        <span className="text-sm font-medium pr-4" style={{ color: textPrimary }}>{q}</span>
        {open
          ? <ChevronUp className="w-4 h-4 flex-shrink-0" style={{ color: accent }} />
          : <ChevronDown className="w-4 h-4 flex-shrink-0 opacity-40" style={{ color: textSecondary }} />
        }
      </button>
      <motion.div
        initial={false}
        animate={{ height: open ? 'auto' : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        className="overflow-hidden"
      >
        <p className="pb-4 text-sm leading-relaxed" style={{ color: textSecondary }}>{a}</p>
      </motion.div>
    </div>
  );
}

export default function FaqAccordion({ section, data, theme }) {
  const anim = section.animation || {};
  const items = data.items || [];

  const isDark = isColorDark(theme.bg);
  const cardBg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)';
  const border = isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.06)';

  const defaultFAQ = [
    { q: 'What is the dress code?', a: 'Semi-formal or cocktail attire.' },
    { q: 'Can I bring a plus one?', a: 'Please refer to your invitation for guest details.' },
    { q: 'Is there parking available?', a: 'Yes, complimentary parking is available at the venue.' },
    { q: 'Will there be a vegetarian option?', a: 'Yes! Please mention dietary requirements in the RSVP form.' },
  ];
  const faqs = items.length > 0 ? items : defaultFAQ;

  return (
    <section className="px-5 py-20" style={{ background: cardBg }} data-testid="faq-section">
      <div className="max-w-sm mx-auto">
        <AnimatedSection preset={anim.section || 'fade-up'}>
          <p
            className="text-xs tracking-[0.3em] uppercase mb-8 text-center"
            style={{ color: theme.accent, fontFamily: theme.eyebrowFont }}
          >
            Questions & Answers
          </p>
        </AnimatedSection>
        <AnimatedSection preset={anim.section || 'fade-up'} delay={0.1}>
          <div>
            {faqs.map((item, i) => (
              <FAQItem
                key={i}
                q={item.q || item.question}
                a={item.a || item.answer}
                accent={theme.accent}
                textPrimary={theme.textPrimary}
                textSecondary={theme.textSecondary}
                border={border}
              />
            ))}
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}

function isColorDark(hex) {
  if (!hex) return false;
  const c = hex.replace('#', '');
  if (c.length < 6) return false;
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 < 128;
}
